import { createContext, ReactNode, useCallback, useContext, useMemo, useState } from 'react';

export interface AsideConfig {
  title: string;
  content: ReactNode;
}

interface LayoutContextValue {
  asideOpen: boolean;
  setAsideOpen: (open: boolean) => void;
  asideTitle: string | null;
  asideContent: ReactNode | null;
  registerAside: (config: AsideConfig | null) => void;
  asideTab: string | null;
  setAsideTab: (tab: string | null) => void;
}

const LayoutContext = createContext<LayoutContextValue | null>(null);

export function LayoutProvider({ children }: Readonly<{ children: ReactNode }>) {
  const [asideOpen, setAsideOpen] = useState(true);
  const [aside, setAside] = useState<AsideConfig | null>(null);
  const [asideTab, setAsideTab] = useState<string | null>(null);

  const registerAside = useCallback((config: AsideConfig | null) => {
    setAside(config);
    if (config !== null) {
      setAsideOpen(true);
    }
    // NOTE: do not reset ``asideTab`` here. Pages re-register the aside
    // whenever their content props change (e.g. feedback context updates
    // on Report.page); resetting the tab on every re-register would
    // clobber a tab change requested synchronously by the same event
    // that produced the new content.
  }, []);

  const value = useMemo<LayoutContextValue>(
    () => ({
      asideOpen: aside !== null && asideOpen,
      setAsideOpen,
      asideTitle: aside?.title ?? null,
      asideContent: aside?.content ?? null,
      registerAside,
      asideTab,
      setAsideTab,
    }),
    [aside, asideOpen, asideTab, registerAside]
  );

  return <LayoutContext.Provider value={value}>{children}</LayoutContext.Provider>;
}

export function useLayout(): LayoutContextValue {
  const context = useContext(LayoutContext);
  if (!context) {
    throw new Error('useLayout must be used within a LayoutProvider');
  }
  return context;
}

export function useLayoutOptional(): LayoutContextValue | null {
  return useContext(LayoutContext);
}

/**
 * An aside slot of a host's own — the case workbench's right column. What the
 * subtree inside `ScopedAside` registers goes to the host, never the app
 * shell's aside: a component that registers the client's chat or a partner's
 * shortlist does so exactly as it does on its own page, and the workbench
 * decides where it shows.
 */
export interface AsideSlot {
  /** What's registered, or null. */
  aside: AsideConfig | null;
  /** Registered and not hidden. */
  open: boolean;
  setOpen: (open: boolean) => void;
  /** Moves each time the subtree asks for the aside to be shown (a partner
   * card clicked) — the host can bring it in front of what else it holds. */
  shownAt: number;
  /** The layout context the subtree reads. */
  value: LayoutContextValue;
}

export function useAsideSlot(): AsideSlot {
  const [aside, setAside] = useState<AsideConfig | null>(null);
  const [open, setOpenState] = useState(true);
  const [shownAt, setShownAt] = useState(0);
  const [asideTab, setAsideTab] = useState<string | null>(null);

  const registerAside = useCallback((config: AsideConfig | null) => {
    setAside(config);
    if (config !== null) {
      setOpenState(true);
    }
  }, []);

  const setOpen = useCallback((next: boolean) => setOpenState(next), []);

  // The subtree's own request to show it: open, and say so to the host.
  const setAsideOpen = useCallback((next: boolean) => {
    setOpenState(next);
    if (next) {
      setShownAt((at) => at + 1);
    }
  }, []);

  const value = useMemo<LayoutContextValue>(
    () => ({
      asideOpen: aside !== null && open,
      setAsideOpen,
      asideTitle: aside?.title ?? null,
      asideContent: aside?.content ?? null,
      registerAside,
      asideTab,
      setAsideTab,
    }),
    [aside, open, asideTab, registerAside, setAsideOpen]
  );

  return { aside, open: aside !== null && open, setOpen, shownAt, value };
}

/** Gives `children` the slot's layout context in place of the app shell's. */
export function ScopedAside({
  slot,
  children,
}: Readonly<{ slot: AsideSlot; children: ReactNode }>) {
  return <LayoutContext.Provider value={slot.value}>{children}</LayoutContext.Provider>;
}
